import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * HeroRailProvider — pulls the hero rail image collection from the backend.
 * Silent-fail: if the endpoint is unreachable, we simply return an empty list
 * so the hero renders without the curved rail (never blocks the page).
 */

const HeroRailContext = createContext({
    items: [],
    loaded: false,
    refresh: () => {},
});

export const HeroRailProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const fetchOnce = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/hero-rail`, { timeout: 12000 });
            const list = Array.isArray(res.data?.items) ? res.data.items : [];
            setItems(list);
        } catch (_e) {
            // Silent — the hero simply omits the rail on failure.
            setItems([]);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        fetchOnce().catch(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, [fetchOnce]);

    const value = useMemo(() => ({ items, loaded, refresh: fetchOnce }), [items, loaded, fetchOnce]);
    return <HeroRailContext.Provider value={value}>{children}</HeroRailContext.Provider>;
};

export const useHeroRail = () => useContext(HeroRailContext);

export default HeroRailProvider;
